import * as React from "react";
import { PlayerSearchResponse, UserGroups } from "@/interfaces";
import { CoachService, SocialCoachSessionService, SubscriptionServices } from "@/services";
import { convertToAdminPlayerExport, getFullName, getLargeSubscriptionType, printBasicShortDate, URLS } from "@/utils";
import UserTable from "../../UserTable";
import { SocialNetworksSection } from "@/components";
import { useState } from "react";
import { AppContext } from "@/providers";
import { DeletePlayerModal } from "./DeletePlayerModal";
import { UpdateEmailModal } from "../UpdateEmailModal";
import { ClientNotesModal } from "@/components/ClientNotesModal";
import { RequestPWModal } from "@/containers/Admin/RequestPWModal";
import { Button, Table, DropdownMenu } from "@/components/ui";
import { cn } from "@/lib/utils";
import copy from "copy-to-clipboard";
import { Pencil, FileText, Lock, Trash2, MoreHorizontal, Copy, Check, HatGlasses } from "lucide-react";

interface OwnProps {}
type Props = OwnProps;

const PlayersListFC: React.FC<Props> = () => {
  const { userContext } = React.useContext(AppContext);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSearchResponse>();
  const [playerModal, setPlayerModal] = useState(false);
  const [deletePlayerModal, setDeletePlayerModal] = useState(false);
  const [notesModal, setNotesModal] = useState(false);
  const [pwModal, setPWModal] = useState(false);

  // Unified copy state handlers for lists
  const [copiedIdValue, setCopiedIdValue] = useState<string | null>(null);
  const [copiedEmailValue, setCopiedEmailValue] = useState<string | null>(null);

  const handleCopyId = (id: string | number) => {
    const idStr = id.toString();
    copy(idStr);
    setCopiedIdValue(idStr);
    setTimeout(() => setCopiedIdValue(null), 1500);
  };

  const handleCopyEmail = (email: string) => {
    copy(email);
    setCopiedEmailValue(email);
    setTimeout(() => setCopiedEmailValue(null), 1500);
  };

  const impersonateCoach = (player: PlayerSearchResponse) => {
    CoachService.getById(player.coachProfile!!.coachId.toString()).then(response => {
      SocialCoachSessionService.impersonate(response.user.email!!).then(() => {
        // Redirect to coach dashboard
        window.location.assign(URLS.coach.dashboard);
      });
    });
  };

  const impersonatePlayer = (player: PlayerSearchResponse) => {
    SocialCoachSessionService.impersonate(player.email!!).then(() => {
      // Redirect to player dashboard
      window.location.assign(URLS.player.dashboard);
    });
  };

  const getActions = (player: PlayerSearchResponse) => {
    const list = [
      { key: "edit", content: "Edit email" },
      { key: "notes", content: "Edit Notes" },
      { key: "pw", content: "Generate Password Link" },
      { key: "impersonate_coach", content: "Impersonate Coach" },
      { key: "impersonate", content: "Impersonate Player" },
    ];

    if (
      player.subscriptionSummary?.status === "CANCELED" &&
      userContext?.account.scGroups?.includes(UserGroups.SYSTEM_ADMIN)
    ) {
      list.push({ key: "delete", content: "Delete Player" });
    }
    return list;
  };

  const handleAction = (index: number, player: PlayerSearchResponse): void => {
    const actions = getActions(player);
    const action = actions[index];
    setSelectedPlayer(player);
    switch (action.key) {
      case "edit":
        setPlayerModal(true);
        break;
      case "notes":
        setNotesModal(true);
        break;
      case "pw":
        setPWModal(true);
        break;
      case "impersonate_coach":
        impersonateCoach(player);
        break;
      case "impersonate":
        impersonatePlayer(player);
        break;
      case "delete":
        setDeletePlayerModal(true);
        break;
    }
  };

  return (
    <>
      <UserTable
        headers={[
          { title: "Player" },
          { title: "Socials" },
          { title: "Subscription" },
          { title: "Coach" },
          { title: "Actions" },
        ]}
        queryFunction={query => SubscriptionServices.searchAllSubscriptions(query.searchTerm, query.page, query.size)}
        exportMapper={(player: PlayerSearchResponse) => convertToAdminPlayerExport(player)}
        renderRow={(player: PlayerSearchResponse) => {
          const isCopiedId = copiedIdValue === player.playerId?.toString();
          const isCopiedEmail = copiedEmailValue === player.email;

          const actions = getActions(player);

          return (
            <React.Fragment key={`player-row-${player.playerId}`}>
              {/* Column 1: Player Details */}
              <Table.Cell className="py-3">
                <div className="flex flex-col gap-1.5">
                  <div className="font-semibold text-sm text-slate-700 flex items-center gap-1.5 flex-wrap">
                    <span>
                      {getFullName(player.firstName, player.lastName) || (
                        <span className="italic text-slate-300/40">No Name</span>
                      )}
                    </span>
                    <span className="text-slate-300">•</span>
                    <span
                      onClick={() => handleCopyId(player.playerId)}
                      className={cn(
                        "text-[9.5px] font-mono font-medium text-slate-500 bg-slate-50 border border-slate-200/60 px-1 rounded cursor-pointer hover:text-slate-800 flex items-center gap-0.5 transition-colors select-none",
                        isCopiedId && "text-green-600 hover:text-green-600 border-green-200 bg-green-50/40"
                      )}
                      title="Click to copy ID"
                    >
                      {player.playerId}
                      {isCopiedId ? (
                        <Check className="size-2.5 text-green-500 shrink-0" />
                      ) : (
                        <Copy className="size-2.5 opacity-40 hover:opacity-100 shrink-0 transition-opacity ml-0.5" />
                      )}
                    </span>
                  </div>

                  <div
                    onClick={() => player.email && handleCopyEmail(player.email)}
                    className={cn(
                      "text-xs text-slate-500 leading-none flex items-center gap-0.5 cursor-pointer hover:text-slate-800 transition-colors select-none w-max",
                      isCopiedEmail && "text-green-600 hover:text-green-600"
                    )}
                    title="Click to copy email"
                  >
                    <span>{player.email}</span>
                    {isCopiedEmail ? (
                      <Check className="size-2.5 text-green-500 shrink-0" />
                    ) : (
                      <Copy className="size-2.5 opacity-40 hover:opacity-100 shrink-0 transition-opacity ml-0.5" />
                    )}
                  </div>

                  {/* Phone number removed */}
                </div>
              </Table.Cell>

              {/* Column 2: Social Networks */}
              <Table.Cell className="py-3 border-l border-slate-100">
                <div className="flex items-center justify-start">
                  <SocialNetworksSection
                    actives={player.socialNetworks?.map(sn => sn.networkId)}
                    allSocialNetworks={["FB", "LI", "IG", "IG_STORY", "TW", "TT", "GL", "ZL"]}
                    size="micro"
                  />
                </div>
              </Table.Cell>

              {/* Column 3: Subscription Info */}
              <Table.Cell className="py-3 border-l border-slate-100">
                <div className="flex flex-col gap-1 text-[11px]">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold text-slate-800 text-sm leading-none">
                      {getLargeSubscriptionType(player.subscriptionSummary?.tier)}
                    </span>
                    {player.subscriptionSummary?.status === "ACTIVE" ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide leading-none bg-emerald-50 text-emerald-700 border border-emerald-200/40">
                        <span className="size-1 rounded-full bg-emerald-500 shrink-0" />
                        {player.subscriptionSummary.status}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide leading-none bg-slate-50 text-slate-600 border border-slate-200/40">
                        <span className="size-1 rounded-full bg-slate-400 shrink-0" />
                        {player.subscriptionSummary?.status || "Canceled"}
                      </span>
                    )}
                  </div>
                  {player.lastLoginDate && (
                    <div className="text-slate-400 mt-1 text-xs">
                      Last Login:{" "}
                      <span className="font-semibold text-slate-600">{printBasicShortDate(player.lastLoginDate)}</span>
                    </div>
                  )}
                </div>
              </Table.Cell>

              {/* Column 3: Coach Info */}
              <Table.Cell className="py-3 border-l border-slate-100">
                {player.coachProfile ? (
                  <div className="flex flex-col gap-1">
                    {/* ID Badge & Company Name at the top */}
                    <div className="flex items-center gap-1.5 flex-wrap leading-none">
                      <span className="text-sm font-bold text-slate-800 tracking-tight">
                        {player.coachProfile.companyName ? (
                          player.coachProfile.companyName
                        ) : (
                          <span className="italic text-slate-300 font-medium">No Company Name</span>
                        )}
                      </span>
                      <span className="text-slate-300">•</span>
                      <span
                        onClick={() => handleCopyId(player.coachProfile!.coachId)}
                        className={cn(
                          "text-[9.5px] font-mono font-medium text-slate-500 bg-slate-50 border border-slate-200/60 px-1 rounded cursor-pointer hover:text-slate-800 flex items-center gap-0.5 transition-colors select-none",
                          copiedIdValue === player.coachProfile!.coachId.toString() &&
                            "text-green-600 hover:text-green-600 border-green-200 bg-green-50/40"
                        )}
                        title="Click to copy ID"
                      >
                        {player.coachProfile.coachId}
                        {copiedIdValue === player.coachProfile!.coachId.toString() ? (
                          <Check className="size-2.5 text-green-500 shrink-0" />
                        ) : (
                          <Copy className="size-2.5 opacity-40 hover:opacity-100 shrink-0 transition-opacity ml-0.5" />
                        )}
                      </span>
                    </div>

                    {/* Coach Email */}
                    {player.coachProfile.email && (
                      <div
                        onClick={() => handleCopyEmail(player.coachProfile!.email!)}
                        className={cn(
                          "text-xs text-slate-500 leading-none flex items-center gap-0.5 cursor-pointer hover:text-slate-800 transition-colors select-none w-max",
                          copiedEmailValue === player.coachProfile!.email && "text-green-600 hover:text-green-600"
                        )}
                        title="Click to copy email"
                      >
                        <span>{player.coachProfile.email}</span>
                        {copiedEmailValue === player.coachProfile!.email ? (
                          <Check className="size-2.5 text-green-500 shrink-0" />
                        ) : (
                          <Copy className="size-2.5 opacity-40 hover:opacity-100 shrink-0 transition-opacity ml-0.5" />
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-slate-300 italic">No Assigned Coach</span>
                )}
              </Table.Cell>

              {/* Column 4: Dropdown Actions */}
              <Table.Cell className="py-3 text-right border-l border-slate-100">
                <div className="flex flex-row items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-slate-100 p-0 cursor-pointer outline-none"
                    onClick={() => impersonatePlayer(player)}
                    title="Impersonate"
                  >
                    <HatGlasses className="size-4 shrink-0 text-muted-foreground" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenu.Trigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-slate-100 p-0 cursor-pointer outline-none"
                        >
                          <MoreHorizontal className="size-4 shrink-0 text-muted-foreground" />
                        </Button>
                      }
                    />
                    <DropdownMenu.Content align="end" className="w-52 border border-slate-100 shadow-md z-5000">
                      {actions.map((act, idx) => {
                        const IconComponent = (() => {
                          switch (act.key) {
                            case "edit":
                              return Pencil;
                            case "notes":
                              return FileText;
                            case "pw":
                              return Lock;
                            case "impersonate_coach":
                              return HatGlasses;
                            case "impersonate":
                              return HatGlasses;
                            case "delete":
                              return Trash2;
                            default:
                              return MoreHorizontal;
                          }
                        })();
                        const isDelete = act.key === "delete";
                        return (
                          <DropdownMenu.Item
                            key={act.key}
                            onClick={() => handleAction(idx, player)}
                            variant={isDelete ? "destructive" : "default"}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer"
                          >
                            <IconComponent
                              className={cn(
                                "size-3.5 shrink-0",
                                isDelete ? "text-destructive" : "text-muted-foreground"
                              )}
                            />
                            <span>{act.content}</span>
                          </DropdownMenu.Item>
                        );
                      })}
                    </DropdownMenu.Content>
                  </DropdownMenu>
                </div>
              </Table.Cell>
            </React.Fragment>
          );
        }}
      />
      <UpdateEmailModal
        open={playerModal}
        onClose={() => setPlayerModal(false)}
        currentEmail={selectedPlayer?.email}
        userId={selectedPlayer?.userId}
      />
      <DeletePlayerModal open={deletePlayerModal} onClose={() => setDeletePlayerModal(false)} player={selectedPlayer} />
      <ClientNotesModal open={notesModal} playerId={selectedPlayer?.playerId} onClose={() => setNotesModal(false)} />
      <RequestPWModal open={pwModal} onClose={() => setPWModal(false)} targetEmail={selectedPlayer?.email || ""} />
    </>
  );
};

export const PlayersList = PlayersListFC;
